import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { nexusApi } from '../api/nexus';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Textarea } from '../components/ui/textarea';
import ActivePacksPanel from '../components/studios/ActivePacksPanel';
import ComplianceGuardrails from '../components/studios/ComplianceGuardrails';
import ToneAndCTAGuide from '../components/studios/ToneAndCTAGuide';

const CopywritingStudio = () => {
  const navigate = useNavigate();
  const { isManagement, isAdmin } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState('');
  const [platform, setPlatform] = useState('Facebook');
  const [analysis, setAnalysis] = useState(null);
  const [variants, setVariants] = useState(null);
  const [showActivePacksPanel, setShowActivePacksPanel] = useState(true); // Phase B4
  const [copyText, setCopyText] = useState(''); // For compliance checking
  const [activePacks, setActivePacks] = useState([]); // Store active packs for Tone Guide

  const handleAnalyzeLink = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.copyAnalyze({ link });
      setAnalysis(response.data);
      setStep(2);
    } catch (error) {
      console.error('Failed to analyze link:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateVariants = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.copyGenerate({
        analysis_id: analysis.analysis_id,
        platform
      });
      setVariants(response.data);
      setStep(3);
    } catch (error) {
      console.error('Failed to generate variants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const response = await nexusApi.copyExport({
        variants: ['A', 'B', 'C'],
        format: 'pdf'
      });
      alert(`Copy deck exported! ID: ${response.data.deck_id}`);
    } catch (error) {
      console.error('Failed to export:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <AlertDescription>
          📎 Paste a link to any creative (Facebook ad, TikTok post, etc.)
        </AlertDescription>
      </Alert>

      <div className="space-y-2">
        <label className="text-sm font-medium">Creative Link</label>
        <Input
          type="url"
          placeholder="https://facebook.com/ad/..."
          value={link}
          onChange={(e) => setLink(e.target.value)}
          data-testid="link-input"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium">Target Platform</label>
        <select
          className="w-full p-3 border rounded-lg"
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          data-testid="platform-select"
        >
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
          <option value="TikTok">TikTok</option>
          <option value="Twitter">Twitter/X</option>
        </select>
      </div>

      {/* Copy Text Input with Compliance Checking */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Copy Text (Optional)</label>
        <Textarea
          placeholder="Enter your copy text for real-time tone and compliance analysis..."
          value={copyText}
          onChange={(e) => setCopyText(e.target.value)}
          rows={4}
          data-testid="copy-text-input"
        />
        
        {/* Phase B4: Real-time Compliance Checking */}
        {copyText && (
          <ComplianceGuardrails
            text={copyText}
            studioType="copy"
            productIds={[]}
            campaignId={null}
          />
        )}
        
        {/* Phase B4: Tone & CTA Guide */}
        {copyText && (
          <ToneAndCTAGuide
            text={copyText}
            activePacks={activePacks}
          />
        )}
      </div>

      <Button
        onClick={handleAnalyzeLink}
        disabled={loading || !link.trim()}
        className="w-full bg-emzi-blue hover:bg-blue-600 text-white font-semibold h-12"
        data-testid="analyze-button"
      >
        {loading ? 'Analyzing...' : 'Analyze Creative →'}
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      {analysis && (
        <>
          <Alert className="bg-blue-50 border-blue-200" data-testid="analysis-complete-alert">
            <AlertDescription>
              ✓ Analysis Complete: {analysis.analysis_id}
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detected Elements</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="text-sm space-y-1">
                {analysis.detected_elements?.map((element, index) => (
                  <li key={`item-${index}`} className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> {element}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <div className="bg-gray-50 border rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-600 mb-2">Extracted Text:</p>
            <p className="text-sm">{analysis.extracted_text}</p>
          </div>

          <Button
            onClick={handleGenerateVariants}
            disabled={loading}
            className="w-full"
            data-testid="generate-variants-button"
          >
            {loading ? 'Generating...' : 'Generate A/B/C Variants →'}
          </Button>
        </>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      {variants && (
        <>
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription>
              ✓ {variants.variants?.length} variants generated for {variants.platform}
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            {variants.variants?.map((variant) => (
              <Card key={variant.id} data-testid={`variant-${variant.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-base">Variant {variant.id}</CardTitle>
                    <div className="flex gap-2">
                      <Badge variant="default">Score: {variant.score}</Badge>
                      <Badge 
                        variant={variant.compliance_risk <= 15 ? "default" : "destructive"}
                        className={variant.compliance_risk <= 15 ? "bg-green-600" : ""}
                      >
                        Risk: {variant.compliance_risk}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm whitespace-pre-wrap mb-2">{variant.copy}</p>
                  <p className="text-xs text-gray-500">Characters: {variant.char_count}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {variants.compliance_check && (
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription>
                ✓ All variants passed compliance check
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => { setStep(1); setVariants(null); setAnalysis(null); }}
              className="flex-1"
              data-testid="new-creative-button"
            >
              New Creative
            </Button>
            <Button
              onClick={handleExport}
              disabled={loading}
              className="flex-1"
              data-testid="export-button"
            >
              {loading ? 'Exporting...' : 'Export Copy Deck'}
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="w-full"
            data-testid="back-dashboard-button"
          >
            Back to Dashboard
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="nexus-content overflow-hidden bg-white">
      {/* EMZI Header - Normalized with Dashboard */}
      <div className="px-5 py-4 bg-emzi-blue text-white flex-shrink-0 shadow-xl" style={{background: 'linear-gradient(to bottom, #0e67a2 0%, #0a5a8a 100%)'}}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center border border-white border-opacity-30 shadow-lg">
              <span className="text-white font-black text-2xl">✍️</span>
            </div>
            <div>
              <h1 className="text-xl font-black tracking-wide text-white">Copywriting Studio</h1>
              <p className="text-sm text-white font-medium">Generate A/B/C copy variants</p>
            </div>
          </div>
          <div className="flex flex-col items-end space-y-1">
            {(isManagement() || isAdmin()) && (
              <span className="text-white text-xs font-medium opacity-90" title="System certified under EMZI ISO-Aligned Compliance Protocol">
                🟢 ISO Compliance Active
              </span>
            )}
            <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm" className="border-2 border-white border-opacity-50 text-white hover:bg-white hover:bg-opacity-20 font-semibold px-3 py-1 text-xs touch-target transition-all duration-200 hover:scale-105" data-testid="back-button">
              ← Back
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto panel-scroll px-4 pt-3 pb-0" style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
        {/* Active Packs Panel - Phase B4 */}
        {showActivePacksPanel && (
          <div style={{ 
            width: '320px', 
            flexShrink: 0, 
            height: 'calc(100vh - 140px)', 
            position: 'sticky', 
            top: '12px',
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}>
            <ActivePacksPanel
              studioType="copy"
              productIds={[]}
              campaignId={null}
              onPacksLoaded={(packs) => setActivePacks(packs)}
            />
          </div>
        )}
        
        {/* Main Content Card */}
        <div style={{ flex: 1, minWidth: 0 }}>
        <Card className="shadow-xl border-0" style={{boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 16px rgba(0, 0, 0, 0.06)'}} data-testid="copy-studio-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emzi-blue bg-opacity-10 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-emzi-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <CardTitle className="text-gray-900 font-bold">Copywriting Studio</CardTitle>
                <CardDescription className="text-sm text-gray-600">Generate A/B/C copy variants</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    s <= step ? 'bg-emzi-blue' : 'bg-gray-200'
                  }`}
                  data-testid={`progress-step-${s}`}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </CardContent>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default CopywritingStudio;
